
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Client } from '@/components/clients/types';
import { toast } from 'sonner';
import { BackendProgressStage } from '@/components/clients/enrichment-dialog/enrichmentTypes';

interface UseClientRealtimeProps {
  clientId: string | undefined;
  currentClient: Client | undefined | null;
  refetchClient: () => void;
}

export const useClientRealtime = ({ clientId, currentClient, refetchClient }: UseClientRealtimeProps) => {
  useEffect(() => {
    if (!clientId) return;

    const clientName = currentClient?.name || 'Client';

    // Channel for client table updates
    const clientChannel = supabase
      .channel(`client-${clientId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'clients',
          filter: `id=eq.${clientId}`,
        },
        async (payload) => {
          console.log('Client table updated via realtime:', payload.new);
          const updatedClient = payload.new as Client;
          
          // This toast remains relevant for the overall status on the client record
          if (updatedClient.enrichment_status === 'available' && 
              currentClient?.enrichment_status !== 'available') {
            toast.success(`${clientName} enrichment data is now available!`, {
              description: 'You can view and apply the results.',
              duration: 6000,
            });
          }
          
          await refetchClient();
        }
      )
      .subscribe();
    
    // Channel for client_enrichment_results table updates
    const enrichmentChannel = supabase
      .channel(`client-enrichment-results-${clientId}`) // Ensure unique channel name
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'client_enrichment_results',
          filter: `client_id=eq.${clientId}`,
        },
        async (payload) => {
          console.log('Client enrichment result updated via realtime:', payload.new);
          
          const enrichmentResult = payload.new as { 
            status: string; 
            progress_stage: BackendProgressStage; 
            error_message?: string; 
            run_id: string;
          };

          // Only show toasts for the latest run_id related to the client
          if (currentClient?.last_enrichment_run_id && enrichmentResult.run_id !== currentClient.last_enrichment_run_id) {
            console.log(`Realtime update for older run_id ${enrichmentResult.run_id}, current is ${currentClient.last_enrichment_run_id}. Skipping toast.`);
            // We might still want to refetch if it's an older run that just got an error for some reason.
            if (enrichmentResult.progress_stage?.startsWith('error')) {
                 await refetchClient(); // Refetch to update history or clear pending states if necessary
            }
            return;
          }
          
          switch (enrichmentResult.progress_stage) {
            case 'initiated_lutra':
              toast.info(`Enrichment for ${clientName} has been initiated.`, {
                description: "We're contacting the enrichment service."
              });
              break;
            case 'pending_lutra_data':
              toast.info(`Enrichment for ${clientName} is awaiting data.`, {
                description: "The enrichment service is processing the request."
              });
              break;
            case 'polling_for_results':
              toast.info(`Polling for ${clientName} results...`, {
                description: "Checking for updates from the enrichment service."
              });
              break;
            case 'processing_retrieved_data':
              toast.info(`Processing data for ${clientName}.`, {
                description: "Retrieved data is being prepared."
              });
              break;
            case 'completed':
              // This case is typically handled by the client table update (enrichment_status: 'available')
              // but a specific toast here can be good if that's delayed or for direct confirmation.
              // The client table update might trigger a more prominent "View Results" type notification.
              // Let's ensure this doesn't double-notify excessively with the client table update toast.
              // The client table's `enrichment_status: 'available'` is the main trigger for "data is ready".
              // This specific toast can confirm the backend process completion.
              if (currentClient?.enrichment_status !== 'available') { // Only if client table hasn't caught up
                 toast.success(`Enrichment for ${clientName} has completed processing!`, {
                   description: "Data will be marked as available shortly."
                 });
              }
              break;
            case 'error_initiating':
            case 'error_polling':
            case 'error_processing':
            case 'error_unknown':
              toast.error(`Enrichment Error for ${clientName}`, {
                description: enrichmentResult.error_message || `An error occurred during the '${enrichmentResult.progress_stage}' stage.`
              });
              break;
            // No toast for 'requested' or 'applied' or 'cancelled' via realtime by default, can be added if needed
          }
          
          await refetchClient(); // Always refetch to get the latest state
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(clientChannel);
      supabase.removeChannel(enrichmentChannel);
    };
  }, [clientId, refetchClient, currentClient]);
};
